import cx from 'classnames';
import { JSX } from 'react';

export type Level = 1 | 2 | 3 | 4 | 5 | 6;
export type Size = '3xl' | '2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';

type HeadingTag = { as?: 'p' | 'span'; level?: never } | { as?: undefined; level: Level };

type BaseHeadingProps = {
  /** Font Size for the Heading component */
  size?: Size;
} & HeadingTag;

type HeadingElement = HTMLHeadingElement | HTMLDivElement | HTMLParagraphElement | HTMLSpanElement;
type HeadingProps = React.HTMLAttributes<HeadingElement> &
  BaseHeadingProps & { ref?: React.Ref<HeadingElement> };

export function Heading({
  children,
  className,
  level,
  size = 'xl',
  as,
  ref,
  ...rest
}: React.PropsWithChildren<HeadingProps>) {
  'use no memo';
  let Tag: keyof JSX.IntrinsicElements;
  if (as === 'p') {
    Tag = 'p';
  } else if (as === 'span') {
    Tag = 'span';
  } else if (level && level <= 6) {
    Tag = `h${level}` as const;
  } else {
    Tag = 'div';
  }

  return (
    <Tag
      {...rest}
      ref={ref as never}
      className={cx('heading--base', headingSizes[size], className)}
    >
      {children}
    </Tag>
  );
}

const headingSizes = {
  xs: 'heading--xs',
  sm: 'heading--sm',
  md: 'heading--md',
  lg: 'heading--lg',
  xl: 'heading--xl',
  '2xl': 'heading--2xl',
  '3xl': 'heading--3xl',
} as const satisfies Record<Size, string>;
